import {Component, Input, OnChanges} from '@angular/core';
import {TranslocoService} from '@jsverse/transloco';

import {StatisticsDataQuality} from '../../models/statistics.model';

/** Ein Zaehler der Datenqualitaet mit Erklaerung, warum er stoert. */
interface DataQualityMetric {
  count: number;
  label: string;
  description: string;
  /**
   * Worauf sich der Zaehler bezieht. Die Zaehler sind nicht einheitlich:
   * Eintraege ohne Datum gehoeren zu keinem Jahr und sind deshalb in jedem
   * Jahr gleich, die uebrigen zaehlen den gewaehlten Zeitraum.
   */
  scope: string;
}

/** Verteilung der Tiere mit Abgang ohne Bestand auf die Tiergruppen. */
interface DataQualityCategory {
  label: string;
  count: number;
  /** Anteil am groessten Wert der Liste, 0-100. */
  percent: number;
}

/** Wie viele Tiernummern zunaechst gezeigt werden. */
const INITIAL_ID_COUNT = 30;

@Component({
  selector: 'app-statistics-data-quality',
  standalone: false,
  templateUrl: './statistics-data-quality.component.html',
  styleUrls: ['./statistics-data-quality.component.scss']
})
export class StatisticsDataQualityComponent implements OnChanges {
  @Input({required: true}) dataQuality!: StatisticsDataQuality;
  /** Gewaehltes Jahr - die Zaehler beziehen sich teils darauf, teils nicht. */
  @Input({required: true}) year!: number;

  metrics: DataQualityMetric[] = [];
  categories: DataQualityCategory[] = [];
  visibleIds: number[] = [];

  showAllIds = false;

  constructor(private readonly _translocoService: TranslocoService) {
  }

  ngOnChanges(): void {
    this.showAllIds = false;
    this.buildMetrics();
    this.buildCategories();
    this.applyIds();
  }

  get hasMoreIds(): boolean {
    return this.dataQuality.affectedAnimalIds.length > this.visibleIds.length;
  }

  showAll(): void {
    this.showAllIds = true;
    this.applyIds();
  }

  private buildMetrics(): void {
    const t = (key: string, params?: object) =>
      this._translocoService.translate(`statistics.dataQuality.${key}`, params);
    const quality = this.dataQuality;

    const inYear = t('scopeYear', {year: this.year});
    const noYear = t('scopeNoYear');

    this.metrics = [
      {
        count: quality.animalsWithDepartureWhileNotInStock,
        label: t('departureWithoutStock'),
        description: t('departureWithoutStockHint'),
        scope: inYear
      },
      {
        count: quality.admissionsWithoutDate,
        label: t('admissionsWithoutDate'),
        description: t('withoutDateHint'),
        scope: noYear
      },
      {
        count: quality.admissionsWithoutType,
        label: t('admissionsWithoutType'),
        description: t('admissionsWithoutTypeHint'),
        scope: inYear
      },
      {
        count: quality.whereaboutsWithoutDate,
        label: t('whereaboutsWithoutDate'),
        description: t('withoutDateHint'),
        scope: noYear
      },
      {
        count: quality.whereaboutsWithoutType,
        label: t('whereaboutsWithoutType'),
        description: t('whereaboutsWithoutTypeHint'),
        scope: ''
      }
    ];
  }

  private buildCategories(): void {
    const entries = this.dataQuality.byCategory
      .filter(entry => entry.animalsWithDepartureWhileNotInStock > 0)
      .sort((a, b) => b.animalsWithDepartureWhileNotInStock - a.animalsWithDepartureWhileNotInStock);

    const max = Math.max(1, ...entries.map(entry => entry.animalsWithDepartureWhileNotInStock));

    this.categories = entries.map(entry => ({
      label: entry.categoryLabel,
      count: entry.animalsWithDepartureWhileNotInStock,
      percent: (entry.animalsWithDepartureWhileNotInStock / max) * 100
    }));
  }

  private applyIds(): void {
    const ids = this.dataQuality.affectedAnimalIds ?? [];
    this.visibleIds = this.showAllIds ? ids : ids.slice(0, INITIAL_ID_COUNT);
  }
}
